import { Dispatch, FormEvent, SetStateAction, useEffect, useState } from "react";

import { ProjectIssuanceTransactionConstants } from "../../../../../../../constants";
import { PillData, ProjectIssuanceTransactionPanelData } from "../../../../../../../models";
import { createIssuanceTransaction } from "../../../../../../../service/project";
import { GetProjectIssuancesResponse } from "../../../../../../../service/query";
import { ServiceError, Status } from "../../../../../../../service/Shared";
import { InteractionTag } from "../../../../../../../templates/TagsPanel/models";
import { saveTransactionTags } from "../../../../../../../templates/UnitTransactionsPanel/utils/saveTransactionTags";
import { isOwnFormSubmit, useIsLoadingWrapper } from "../../../../../../../utils";
import { Toast } from "../../../../../../../widget";

interface UseMultiIssuanceTransactionPanelProps {
  show: boolean;
  issuances: GetProjectIssuancesResponse["results"];
  onClose: () => void;
  handleRefresh: () => void;
}

interface UseMultiIssuanceTransactionPanelReturnData {
  multiIssuanceTransactionPanelData: ProjectIssuanceTransactionPanelData[];
  setMultiIssuanceTransactionPanelData: Dispatch<SetStateAction<ProjectIssuanceTransactionPanelData[]>>;
  errors: ServiceError[] | undefined;
  isHandleSubmitLoading: boolean;
  handleClosePanel: () => void;
  resetPanel: () => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  tagPills: PillData[][];
  changedTagsExternal: InteractionTag[][];
  setChangedTagsExternal: (index: number) => Dispatch<SetStateAction<InteractionTag[]>>;
  showTagsPanelIndex: number | null;
  setShowTagsPanelIndex: Dispatch<SetStateAction<number | null>>;
}

export const useMultiIssuanceTransactionPanel = ({
  show,
  issuances,
  onClose,
  handleRefresh,
}: UseMultiIssuanceTransactionPanelProps): UseMultiIssuanceTransactionPanelReturnData => {
  const [errors, setErrors] = useState<ServiceError[] | undefined>();
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);
  const [showTagsPanelIndex, setShowTagsPanelIndex] = useState<number | null>(null);
  const [tagPills, setTagPills] = useState<PillData[][]>([]);
  const [changedTagsExternal, setChangedTagsExternalState] = useState<InteractionTag[][]>([]);

  // Use factory to create a setter for changedTagsExternal for a specific index
  const setChangedTagsExternal =
    (index: number): Dispatch<SetStateAction<InteractionTag[]>> =>
    (value) =>
      setChangedTagsExternalState((prev) => {
        const next = [...prev];
        const current = prev[index] ?? [];
        const resolved =
          typeof value === "function" ? (value as (state: InteractionTag[]) => InteractionTag[])(current) : value;
        next[index] = resolved;
        return next;
      });

  // eslint-disable-next-line no-undef
  const [multiIssuanceTransactionPanelData, setMultiIssuanceTransactionPanelData] = useState<
    ProjectIssuanceTransactionPanelData[]
  >([]);

  const resetPanel = (): void => {
    setMultiIssuanceTransactionPanelData(
      issuances.map((issuance) => ({
        ...ProjectIssuanceTransactionConstants.DEFAULT_ISSUANCE_TRANSACTION,
        issuanceUuid: issuance.uuid,
      }))
    );
    setTagPills(issuances.map(() => []));
    setChangedTagsExternalState(issuances.map(() => []));
    setShowTagsPanelIndex(null);
    setErrors([]);
  };

  const handleClosePanel = (): void => {
    onClose();
    setMultiIssuanceTransactionPanelData([]);
    setTagPills([]);
    setChangedTagsExternalState([]);
    setShowTagsPanelIndex(null);
    setErrors([]);
  };
  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!isOwnFormSubmit(e)) return;
    setErrors([]);

    const res = await createIssuanceTransaction({
      issuanceTransactions: multiIssuanceTransactionPanelData.map((issuanceTransactionPanelData) => ({
        areaHa: issuanceTransactionPanelData.areaHa,
        brokerCollaboratorUuid: issuanceTransactionPanelData.brokerCollaboratorUuid,
        counterPartyCollaboratorUuid: issuanceTransactionPanelData.counterPartyCollaboratorUuid,
        expiryDate: issuanceTransactionPanelData.expiryDate,
        externalReference: issuanceTransactionPanelData.externalReference,
        fees: issuanceTransactionPanelData.fees,
        issuanceUuid: issuanceTransactionPanelData.issuanceUuid ?? "",
        parentTransactionUuid: issuanceTransactionPanelData.parentTransactionUuid,
        price: issuanceTransactionPanelData.price,
        quantity: issuanceTransactionPanelData.quantity ?? 0,
        totalValue:
          issuanceTransactionPanelData.totalValue !== null
            ? parseFloat(issuanceTransactionPanelData.totalValue.toFixed(5))
            : null,
        transactionDate: issuanceTransactionPanelData.transactionDate,
        type: issuanceTransactionPanelData.type ?? "",
      })),
    });

    if (res.status === Status.Success) {
      const tagResults = await Promise.all(
        (res.data?.issuanceTransactions ?? []).map((created, index) =>
          saveTransactionTags(created.uuid, changedTagsExternal[index] ?? [])
        )
      );

      Toast.success({ message: "Issuance transactions created successfully" });

      /*
        Each row is tagged independently, so several can fail while the rest succeed. The count is what the user
        can act on — naming one row's error would imply the others were fine.
      */
      const failedTagCount = tagResults.filter((tagErrors) => tagErrors !== undefined).length;
      if (failedTagCount > 0) {
        Toast.error({
          message:
            failedTagCount === 1
              ? "The transactions were created, but the tags on one of them could not be added."
              : `The transactions were created, but the tags on ${failedTagCount} of them could not be added.`,
        });
      }

      handleRefresh();
      handleClosePanel();
    }

    if (res.status === Status.Error) {
      setErrors(res.errors);
    }
  }, setIsHandleSubmitLoading);

  useEffect(() => {
    setTagPills(
      changedTagsExternal.map((tags) =>
        tags.filter((tag) => tag.checked).map((tag) => ({ key: tag.uuid, label: tag.data.name }))
      )
    );
  }, [changedTagsExternal]);

  useEffect(() => {
    if (issuances.length > 0) {
      setMultiIssuanceTransactionPanelData(
        issuances.map((issuance) => ({
          ...ProjectIssuanceTransactionConstants.DEFAULT_ISSUANCE_TRANSACTION,
          issuanceUuid: issuance.uuid,
        }))
      );
      setTagPills(issuances.map(() => []));
      setChangedTagsExternalState(issuances.map(() => []));
      setShowTagsPanelIndex(null);
    }
  }, [issuances, show]);

  return {
    errors,
    isHandleSubmitLoading,
    multiIssuanceTransactionPanelData,
    setMultiIssuanceTransactionPanelData,
    handleClosePanel,
    resetPanel,
    handleSubmit,
    tagPills,
    changedTagsExternal,
    setChangedTagsExternal,
    showTagsPanelIndex,
    setShowTagsPanelIndex,
  };
};
